import { apiClient } from './apiClient';
import type { UtilisationReportData, UtilisationSummary, EmployeeDetailData, ProjectDetailData } from '@/types/utilisation';
import { toLocalDateString } from '@/utils/formatters';

export interface UtilisationQueryParams {
  dateFrom?: Date;
  dateTo?: Date;
  employeeIds?: number[];
  departments?: string[];
  projectIds?: string[];
  includeNonBillable?: boolean;
  groupBy?: 'employee' | 'department' | 'project' | 'week';
}

/**
 * Build query string from parameters
 * Arrays are repeated as multiple query parameters (e.g., employeeIds=E1&employeeIds=E2)
 */
function buildQueryString(params: UtilisationQueryParams): string {
  const queryParts: string[] = [];

  if (params.dateFrom) {
    queryParts.push(`dateFrom=${toLocalDateString(params.dateFrom)}`);
  }
  if (params.dateTo) {
    queryParts.push(`dateTo=${toLocalDateString(params.dateTo)}`);
  }

  if (params.employeeIds && params.employeeIds.length > 0) {
    params.employeeIds.forEach(id => {
      queryParts.push(`employeeIds=${encodeURIComponent(id)}`);
    });
  }
  if (params.departments && params.departments.length > 0) {
    params.departments.forEach(dept => {
      queryParts.push(`departments=${encodeURIComponent(dept)}`);
    });
  }
  if (params.projectIds && params.projectIds.length > 0) {
    params.projectIds.forEach(id => {
      queryParts.push(`projectIds=${encodeURIComponent(id)}`);
    });
  }

  if (params.includeNonBillable !== undefined) {
    queryParts.push(`includeNonBillable=${params.includeNonBillable}`);
  }

  if (params.groupBy) {
    queryParts.push(`groupBy=${encodeURIComponent(params.groupBy)}`);
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

/**
 * Parse date strings in API response to Date objects
 */
function parseUtilisationReportDates(report: any): UtilisationReportData {
  return {
    ...report,
    trends: report.trends?.map((trend: any) => ({
      ...trend,
      weekEnding: trend.weekEnding ? new Date(trend.weekEnding) : trend.weekEnding,
    })) || [],
  };
}

/**
 * Parse date strings in employee detail API response
 */
function parseEmployeeDetailDates(response: any): EmployeeDetailData {
  return {
    employee: response.employee,
    weeklyBreakdown: response.weeklyBreakdown?.map((w: any) => ({
      ...w,
      week: w.week ? formatWeekLabel(w.week) : w.week,
    })) || [],
    projectAllocation: response.projectAllocation || [],
    dailyActivity: response.dailyActivity?.map((d: any) => ({
      ...d,
      date: d.date ? new Date(d.date) : new Date(),
    })) || [],
  };
}

/**
 * Parse date strings in project detail API response
 */
function parseProjectDetailDates(response: any): ProjectDetailData {
  return {
    project: response.project,
    employeeAllocation: response.employeeAllocation || [],
    weeklyTrend: response.weeklyTrend?.map((w: any) => ({
      ...w,
      week: w.week ? formatWeekLabel(w.week) : w.week,
    })) || [],
    timeline: response.timeline?.map((t: any) => ({
      ...t,
      date: t.date ? new Date(t.date) : new Date(),
    })) || [],
  };
}

/**
 * Format a date string (from WeekEnding) into a short label like "May 12"
 */
function formatWeekLabel(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

/**
 * Utilisation API service for utilisation report endpoints
 */
export const utilisationApi = {
  /**
   * Get complete utilisation report with all metrics
   */
  async getUtilisationReport(params: UtilisationQueryParams = {}): Promise<UtilisationReportData> {
    const queryString = buildQueryString(params);
    const response = await apiClient.get<any>(`/reports/utilisation${queryString}`);
    return parseUtilisationReportDates(response);
  },

  /**
   * Get utilisation summary only (lightweight)
   */
  async getUtilisationSummary(params: Omit<UtilisationQueryParams, 'groupBy'>): Promise<UtilisationSummary> {
    const queryString = buildQueryString(params);
    return apiClient.get<UtilisationSummary>(`/reports/utilisation/summary${queryString}`);
  },

  /**
   * Get detailed utilisation data for a single employee
   */
  async getEmployeeDetail(employeeId: number | string, params: { dateFrom?: Date; dateTo?: Date } = {}): Promise<EmployeeDetailData> {
    const queryParts: string[] = [];
    if (params.dateFrom) {
      queryParts.push(`dateFrom=${toLocalDateString(params.dateFrom)}`);
    }
    if (params.dateTo) {
      queryParts.push(`dateTo=${toLocalDateString(params.dateTo)}`);
    }
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const response = await apiClient.get<any>(`/reports/utilisation/employee/${encodeURIComponent(employeeId)}${queryString}`);
    return parseEmployeeDetailDates(response);
  },

  /**
   * Get detailed utilisation data for a single project
   */
  async getProjectDetail(projectId: string, params: { dateFrom?: Date; dateTo?: Date } = {}): Promise<ProjectDetailData> {
    const queryParts: string[] = [];
    if (params.dateFrom) {
      queryParts.push(`dateFrom=${toLocalDateString(params.dateFrom)}`);
    }
    if (params.dateTo) {
      queryParts.push(`dateTo=${toLocalDateString(params.dateTo)}`);
    }
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const response = await apiClient.get<any>(`/reports/utilisation/project/${encodeURIComponent(projectId)}${queryString}`);
    return parseProjectDetailDates(response);
  },
};
